import React from 'react';
import { Link, Navigate, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useParentChild } from '../../context/ParentChildContext';
import ParentChildSwitcher from './ParentChildSwitcher';
import ListPageLayout, { ListLoadingSpinner } from '../layout/ListPageLayout';

/**
 * Ensures a parent has selected an approved child before showing child data pages.
 */
const RequireSelectedChild = () => {
    const { t } = useTranslation();
    const { enabled, loading, loadError, approvedChildren, selectedStudentId, refresh } = useParentChild();

    if (!enabled) return <Outlet />;

    if (loading) {
        return (
            <ListPageLayout>
                <ListLoadingSpinner label={t('common.loading')} />
            </ListPageLayout>
        );
    }

    if (loadError) {
        return (
            <div className="page-shell max-w-lg mx-auto">
                <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center shadow-sm">
                    <h2 className="text-lg font-semibold text-red-900 mb-2">
                        {t('parent.childrenLoadFailed', { defaultValue: 'Could not load linked students' })}
                    </h2>
                    <p className="text-sm text-red-800 mb-4">
                        {t('parent.childrenLoadFailedHint', {
                            defaultValue: 'This is not the same as having no children. Refresh to try again.',
                        })}
                    </p>
                    <button
                        type="button"
                        onClick={() => refresh()}
                        className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                    >
                        {t('common.retry', { defaultValue: 'Retry' })}
                    </button>
                </div>
            </div>
        );
    }

    if (approvedChildren.length === 0) {
        return <Navigate to="/parent" replace />;
    }

    if (!selectedStudentId) {
        return (
            <div className="page-shell max-w-lg mx-auto">
                <div className="rounded-2xl border border-indigo-100 bg-white p-6 shadow-sm text-center">
                    <div className="w-14 h-14 rounded-2xl bg-indigo-100 text-indigo-600 flex items-center justify-center mx-auto mb-4">
                        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('parent.chooseChildTitle')}</h2>
                    <p className="text-sm text-gray-600 mb-5">
                        {t('parent.chooseChildBody')}
                    </p>
                    <ParentChildSwitcher className="justify-center mb-4" />
                    <Link to="/parent" className="text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:underline">
                        {t('parent.backToParentHome')}
                    </Link>
                </div>
            </div>
        );
    }

    return <Outlet />;
};

export default RequireSelectedChild;
